import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { companyKeys } from '@/lib/query-keys';
import { companyService } from '@/services/company.service';
import { useCompanyFilterStore } from '@/stores/companyFilterStore';

export function useCompanies() {
  const queryClient = useQueryClient();
  const filters = useCompanyFilterStore();

  const companiesQuery = useQuery({
    queryKey: companyKeys.list(filters.current),
    queryFn: () => companyService.getAll(filters.current),
    placeholderData: keepPreviousData,
  });

  const createCompany = useMutation({
    mutationFn: (data: Parameters<typeof companyService.create>[0]) => companyService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() });
      toast.success('Company created successfully');
    },
    onError: () => toast.error('Failed to create company'),
  });

  const updateCompany = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof companyService.update>[1] }) =>
      companyService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: companyKeys.detail(id) });
      toast.success('Company updated successfully');
    },
    onError: () => toast.error('Failed to update company'),
  });

  const deleteCompany = useMutation({
    mutationFn: (id: string) => companyService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() });
      toast.success('Company deleted successfully');
    },
    onError: () => toast.error('Failed to delete company'),
  });

  return {
    companies: companiesQuery.data?.data ?? [],
    meta: companiesQuery.data?.meta,
    isLoading: companiesQuery.isLoading,
    filters,
    createCompany,
    updateCompany,
    deleteCompany,
    exportCsv: () => companyService.exportCsv(filters.current),
  };
}

export function useCompany(id: string) {
  const queryClient = useQueryClient();

  const companyQuery = useQuery({ queryKey: companyKeys.detail(id), queryFn: () => companyService.getById(id), enabled: !!id });
  const contactsQuery = useQuery({ queryKey: companyKeys.contacts(id), queryFn: () => companyService.getContacts(id), enabled: !!id });
  const dealsQuery = useQuery({ queryKey: companyKeys.deals(id), queryFn: () => companyService.getDeals(id), enabled: !!id });
  const activitiesQuery = useQuery({ queryKey: companyKeys.activities(id), queryFn: () => companyService.getActivities(id), enabled: !!id });
  const notesQuery = useQuery({ queryKey: companyKeys.notes(id), queryFn: () => companyService.getNotes(id), enabled: !!id });

  const invalidate = () => { queryClient.invalidateQueries({ queryKey: companyKeys.detail(id) }); };

  return {
    company: companyQuery.data?.data,
    isLoading: companyQuery.isLoading,
    contacts: contactsQuery.data?.data ?? [],
    deals: dealsQuery.data?.data ?? [],
    activities: activitiesQuery.data?.data ?? [],
    notes: notesQuery.data?.data ?? [],
    invalidate,
  };
}
